import React, { useRef, useState } from 'react';
import { Box, Typography, Stack, Alert, Menu, MenuItem, Button } from '@mui/material';

import { CustomStepIcon } from '../../components';
import type { CustomStepTemplate } from './TemplateSelector.types.ts';
import { getTemplatesForHealthCategory } from './getTemplatesForHealthCategory.ts';

type TemplateCardProps<T extends CustomStepTemplate> = {
  healthCategoryId: string;
  template: T;
  onSelect: () => void;
};

const TemplateCard = <T extends CustomStepTemplate>({ template, healthCategoryId, onSelect }: TemplateCardProps<T>) => {
  const handleClick = () => {
    onSelect();
  };

  const { name, description } = template.getData({ healthCategoryId });

  return (
    <Stack
      direction="column"
      gap={0.5}
      alignItems="flex-start"
      maxWidth="100%"
      onClick={handleClick}
      sx={{
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      <Stack direction="row" gap={0.5} alignItems="center">
        <CustomStepIcon customStepType={template.type} status={undefined} fontSize="small" color="primary" />
        <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
          {name}
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        sx={{
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {description}
      </Typography>
    </Stack>
  );
};

type SelectMenuItemProps<T extends CustomStepTemplate> = {
  template: T;
  healthCategoryId: string;
  onSelect: (template: NoInfer<T>) => void;
};

const SelectMenuItem = <T extends CustomStepTemplate>({
  template,
  healthCategoryId,
  onSelect,
}: SelectMenuItemProps<T>) => {
  const handleOnSelect = () => {
    onSelect(template);
  };

  return (
    <MenuItem
      key={template.id}
      value={template.type}
      sx={{
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        '&:hover': { backgroundColor: 'transparent' },
      }}
    >
      <TemplateCard template={template} healthCategoryId={healthCategoryId} onSelect={handleOnSelect} />
    </MenuItem>
  );
};

type TemplateSelectorProps = {
  healthCategoryId: string;
  onTemplateSelect: (template: CustomStepTemplate) => void;
};

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({ healthCategoryId, onTemplateSelect }) => {
  const [open, setOpen] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);
  const templates = getTemplatesForHealthCategory(healthCategoryId);

  const handleOnSelect = <T extends CustomStepTemplate>(template: T) => {
    onTemplateSelect(template);
    setOpen(false);
  };

  if (templates.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        There {templates.length === 1 ? 'is' : 'are'} {templates.length} template{templates.length === 1 ? '' : 's'}{' '}
        available for the <span style={{ fontWeight: 'bold' }}>{healthCategoryId}</span> form.
      </Typography>
      <Alert
        icon={false}
        ref={alertRef}
        severity="info"
        action={
          <Button variant="text" size="small" color="primary" onClick={() => setOpen(true)}>
            Select
          </Button>
        }
      >
        Select a template to quickly create a standardised step
      </Alert>

      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={alertRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              width: alertRef.current?.clientWidth,
              maxHeight: '50vh',
            },
          },
        }}
      >
        {templates.map((template) => (
          <SelectMenuItem
            key={template.id}
            template={template}
            onSelect={handleOnSelect}
            healthCategoryId={healthCategoryId}
          />
        ))}
      </Menu>
    </Box>
  );
};
